import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { useSelector } from 'react-redux';
import styles from './OrderSummarySteppedWrapper.module.scss';
import messages from './messages';
import { OrderSummaryDataSources } from '../../../hooks/useOrderDataSource/useOrderDataSource';
import { priceToString } from '../../../utils';
import { OrderInStore } from '../../../slices/orders/types';
import { CartInStore } from '../../../slices/cart/types';

import OrderSummarySteppedSkeleton from '../OrderSummarySteppedSkeleton';
import { REVIEW_STEP } from '../SteppedCheckout/constants';
import useSteppedCheckoutError from '../../../hooks/useSteppedCheckoutError';

type OrderSummaryDataSourcesType = 'cart' | 'order';

interface OrderSummarySteppedWrapperyProps {
  dataSource: OrderSummaryDataSourcesType;
  title: string;
  children: React.ReactNode;
  isOrderConfirmationPage?: boolean;
  isMobileOrderSummary?: boolean;
  isSteppedCheckout?: boolean;
  isSummaryOpenDefault?: boolean;
}

interface RootState {
  cart: CartInStore;
  order: OrderInStore;
}

const OrderSummarySteppedWrapper = ({
  dataSource,
  title,
  children,
  isOrderConfirmationPage,
  isMobileOrderSummary,
  isSummaryOpenDefault,
  isSteppedCheckout,
}: OrderSummarySteppedWrapperyProps) => {
  const { formatMessage, formatNumber } = useIntl();

  const stripeIsExpressButton = localStorage.getItem('stripe_confirmation');

  const { errorMessage } = useSteppedCheckoutError();

  const hideItemCount = dataSource === OrderSummaryDataSources.ORDER;
  const [isSummaryOpen, setIsSummaryOpen] = useState(isSummaryOpenDefault);

  const { currentStep } = useSelector((state: RootState) => state.order);

  const showSkeleton =
    stripeIsExpressButton && currentStep !== REVIEW_STEP && !errorMessage;

  const totalFromStore = useSelector(({ cart }: RootState) => {
    if (cart?.taxedPrice) {
      return cart?.taxedPrice.totalGross;
    }

    const estimatedTaxAmount: number =
      cart?.estimatedTaxAmount?.centAmount ?? 0;
    const totalPriceCentAmount: number = cart?.totalPrice?.centAmount ?? 0;

    return {
      ...cart?.totalPrice,
      centAmount: totalPriceCentAmount + estimatedTaxAmount,
    };
  });

  useEffect(() => {
    setIsSummaryOpen(false);
  }, [currentStep]);

  const handleSummaryDisplay = () => {
    if (!isMobileOrderSummary) {
      setIsSummaryOpen(!isSummaryOpen);
    }
  };

  if (showSkeleton) {
    return <OrderSummarySteppedSkeleton />;
  }

  return (
    <div
      data-testid='component-no-review-step'
      className={classnames(styles['cart-summary-container'], {
        [styles['hide-on-desktop']]:
          !isSteppedCheckout &&
          !isOrderConfirmationPage &&
          !isMobileOrderSummary,
        [styles['no-gap']]:
          !isSummaryOpen && !isMobileOrderSummary && !isOrderConfirmationPage,
        [styles['no-border']]: isMobileOrderSummary,
        [styles['order-confirmation-page']]: isOrderConfirmationPage,
      })}
    >
      <div
        role='button'
        tabIndex={0}
        className={classnames(
          styles['order-summary-title-wrapper'],
          styles['order-summary-title'],
          {
            [styles.hide]: !isSummaryOpen,
            [styles['order-summary-order-complete']]: hideItemCount,
          },
        )}
        onClick={!isOrderConfirmationPage ? handleSummaryDisplay : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isOrderConfirmationPage) {
            handleSummaryDisplay();
          }
        }}
      >
        <div className={styles['title-and-arrow']}>
          <div className={classnames(styles.title)}>
            {title ?? formatMessage(messages.title)}
          </div>
          <div
            role='button'
            tabIndex={0}
            aria-label='show'
            className={classnames(styles['order-summary-arrow'], {
              [styles.up]: !isSummaryOpen,
              [styles.hide]: isMobileOrderSummary || isOrderConfirmationPage,
            })}
            onKeyDown={(e) => {
              e.preventDefault();
              if (e.key === 'Enter') {
                setIsSummaryOpen(!isSummaryOpen);
              }
            }}
          />
          <div
            className={classnames(styles['mobile-subtotal'], {
              [styles['summary-open']]: isSummaryOpen,
              [styles.hide]: isOrderConfirmationPage || isMobileOrderSummary,
            })}
          >
            {priceToString({
              price: totalFromStore,
              formatNumber,
              formatMessage,
              forceDigit: true,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
      </div>

      <div
        className={classnames(styles['order-summary-wrapper'], {
          [styles.hide]:
            !isSummaryOpen && !isMobileOrderSummary && !isOrderConfirmationPage,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default OrderSummarySteppedWrapper;
